import styled, { css } from 'styled-components';

import { ScSelectBlockProps } from './types';
import { getCDNImageUrl } from '@/helpers/cdn';
import { ThemeMode } from '@/helpers/enums';

import { device, hexToRGB } from '@/styles/helpers';

const tickIconUrl = getCDNImageUrl('/common/tickIcon.svg');

interface ScInputProps {
  error: boolean;
  icon: boolean;
  value?: any;
  field?: string;
  mode?: ThemeMode;
  approved?: boolean;
  background?: string;
  inputRightPadding?: number;
}

interface ScSelectNativeProps {
  error: boolean;
}

export const ScInput = styled.input<ScInputProps>`
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme, mode }) =>
    mode === ThemeMode.Dark ? theme.whiteColor : theme.blackColor};
  height: 40px;
  width: 100%;
  border: 2px solid transparent;
  border-radius: 24px;
  padding: ${({ icon }) => (icon ? '8px 16px 8px 36px' : '8px 16px')};
  background-color: ${({ theme, mode, background }) =>
    background
      ? background
      : mode === ThemeMode.Dark
        ? theme.grayDarkColor
        : theme.gray1Color};
  transition:
    padding 0.2s,
    border-color 0.3s;
  appearance: none;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  text-decoration: none;

  ${({ inputRightPadding }) =>
    inputRightPadding &&
    css`
      padding-right: ${inputRightPadding}px;
    `}

  &::placeholder {
    color: ${({ theme }) => theme.blackLightColor};
    transition: color 0.3s;
  }

  &:focus {
    border-color: ${({ theme, mode }) =>
      mode === ThemeMode.Dark ? theme.grayColor : theme.grayMegaLightColor};
    color: ${({ theme, mode }) =>
      mode === ThemeMode.Dark ? theme.whiteColor : theme.blackColor};

    &::placeholder {
      color: ${({ theme, mode }) =>
        mode === ThemeMode.Dark ? theme.whiteColor : theme.blackColor};
    }

    @media (hover: none) {
      &[name='search']::placeholder {
        color: ${({ theme }) => theme.blackLightColor};
      }
    }
  }

  &:disabled {
    color: ${({ theme, mode, approved }) =>
      !approved &&
      (mode === ThemeMode.Dark
        ? hexToRGB(theme.whiteColor, 0.2)
        : hexToRGB(theme.blackColor, 0.2))};

    &::placeholder {
      color: ${({ theme, mode }) =>
        mode === ThemeMode.Dark
          ? hexToRGB(theme.whiteColor, 0.2)
          : theme.blackLightColor};
    }
  }

  ${({ error }) =>
    error &&
    css`
      border-color: ${({ theme }) => theme.redColor};
    `}

  ${({ approved, error, mode }) =>
    approved &&
    css`
      border-color: ${({ theme }) =>
        error ? theme.redColor : theme.greenColor};
      color: ${({ theme }) =>
        mode === ThemeMode.Dark ? theme.whiteColor : theme.blackColor};
      font-size: 16px;
      font-weight: 700;
      line-height: 24px;
      letter-spacing: 0.16px;
    `}

  @media (hover: hover) {
    &:hover {
      border-color: ${({ theme, value, error, mode, approved }) =>
        value &&
        !error &&
        !approved &&
        (mode === ThemeMode.Dark ? theme.grayColor : theme.grayMegaLightColor)};
      &::placeholder {
        color: ${({ theme, mode }) =>
          mode === ThemeMode.Dark ? theme.whiteColor : theme.blackColor};
      }
    }
  }
  &::-webkit-search-decoration,
  &::-webkit-search-cancel-button,
  &::-webkit-search-results-button,
  &::-webkit-search-results-decoration {
    display: none;
  }
`;

export const ScInputIcon = styled.img`
  position: absolute;
  top: 8px;
  left: 7px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
`;

export const ScInputInternalLabel: any = styled.span`
  position: absolute;
  top: 8px;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  text-align: right;

  color: ${({ theme }) => theme.blackLightColor};
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0.16px;
`;

export const ScSelectBlock = styled.div<ScSelectBlockProps>`
  position: relative;
  z-index: ${({ active }) => (active ? 2 : 0)};

  & > .select-button {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: flex-start;
    align-items: center;
    min-width: 0;

    font-size: 16px;
    line-height: 24px;
    font-weight: ${({ isInputAddon }) => (isInputAddon ? 700 : 400)};
    color: ${({ theme, error, hideInputErrorColor, disabled, mode }) =>
      disabled
        ? hexToRGB(theme.blackColor, 0.6)
        : error && !hideInputErrorColor
          ? theme.redColor
          : mode === ThemeMode.Dark
            ? theme.whiteColor
            : theme.blackColor};
    height: ${({ isInputAddon }) => (isInputAddon ? '32px' : '40px')};
    width: 100%;
    border: ${({ theme, active, isInputAddon, error }) =>
      `2px solid ${
        error
          ? theme.redColor
          : active && !isInputAddon
            ? theme.grayMegaLightColor
            : 'transparent'
      }`};
    border-radius: 24px;
    padding: 8px 16px;
    background-color: ${({ theme, mode, isInputAddon }) =>
      mode === ThemeMode.Dark
        ? theme.grayDarkColor
        : isInputAddon
          ? theme.whiteColor
          : theme.gray1Color};
    transition: border-color 0.2s;
    cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};

    & > .value {
      padding-right: 30px;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      max-width: 20ch;

      &.with-icon {
        position: relative;

        img {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          margin-right: 8px;
        }
      }

      .placeholder {
        color: ${({ theme, disabled }) =>
          disabled ? theme.grayMegaLightColor : theme.blackLightColor};
      }
    }
    & > .arrow {
      position: absolute;
      right: 16px;
      top: 50%;
      transition: transform 0.5s;
      transform: ${({ active }) =>
        `translateY(-50%) ${active ? 'rotate(0deg)' : 'rotate(180deg)'}`};

      svg {
        display: block;
        path {
          stroke: ${({ theme }) => theme.grayColor};
        }
      }
    }

    @media (hover: hover) {
      &:hover {
        border-color: ${({ theme, isInputAddon }) =>
          isInputAddon ? 'transparent' : theme.grayMegaLightColor};

        & > .value {
          .placeholder {
            color: ${({ theme }) => theme.blackColor};
          }
        }

        & > .arrow {
          svg {
            path {
              stroke: ${({ theme }) => theme.blackColor};
            }
          }
        }
      }
    }
  }
`;

export const ScSelectNative = styled.div<ScSelectNativeProps>`
  position: relative;
  & > select {
    height: 40px;
    width: 100%;
    border: none;
    border-radius: 28px;
    font-size: 16px;
    line-height: 24px;
    color: ${({ theme }) => theme.blackColor};
    cursor: pointer;
    padding: 0 14px;
    appearance: none;
    white-space: nowrap;
    text-overflow: ellipsis;
    padding-right: 39px;
    background-color: ${({ theme }) => theme.gray1Color};

    ${({ error, theme }) =>
      error &&
      css`
        border: 2px solid ${theme.redColor};
      `}

    &:focus {
      outline: none;
    }
  }
  & > .arrow {
    position: absolute;
    right: 18px;
    top: 18px;
    transition: transform 0.2s;
    transform: rotate(180deg);
    svg {
      display: block;
      path {
        stroke: ${({ theme }) => theme.grayColor};
      }
    }
  }
`;

export interface ScOptionsProps {
  dropdownUp?: boolean;
  minOptionsWidth?: number;
  optionsPosition?: string;
  search?: boolean;
}

export const ScOptions = styled.div<ScOptionsProps>`
  position: absolute;
  min-width: ${({ minOptionsWidth }) =>
    minOptionsWidth ? `${minOptionsWidth}px` : 'auto'};

  ${({ dropdownUp }) =>
    dropdownUp
      ? css`
          bottom: 100%;
          margin-bottom: 8px;
        `
      : css`
          top: 100%;
          margin-top: 8px;
        `}
  left: 0;
  right: 0;
  inset: ${({ optionsPosition }) => optionsPosition ?? 'auto 0'};
  padding: 0px 16px;
  background: ${({ theme }) => theme.gray1Color};
  box-shadow: ${({ theme }) =>
    `0px 2px 10px ${hexToRGB(theme.blackColor, 0.3)}`};
  border-radius: 24px;
  overflow: hidden;

  ul {
    display: block;
    list-style: none;

    & > li {
      padding: 8px 0;
      cursor: pointer;
      background-color: transparent;
      color: ${({ theme }) => theme.blackLightColor};
      transition: color 0.2s;

      line-height: 24px;
      display: flex;
      position: relative;

      &.active .content {
        width: calc(100% - 32px);
      }

      .content {
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        line-height: 24px;
      }

      svg {
        width: 24px;
        position: absolute;
        right: 2px;
      }

      &.not-found {
        text-align: center;
        font-size: 12px;
      }

      &.with-icon {
        position: relative;

        & > .content {
          line-height: 1;
        }

        img {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          margin-right: 8px;
        }
      }
      &.active,
      &:hover {
        color: ${({ theme }) => theme.blackColor};
      }

      &:first-child {
        padding-top: ${({ search }) => (search ? 0 : '16px')};
      }

      &:last-child {
        padding-bottom: 16px;
      }
    }
  }
`;

export const ScCheckboxBlock = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-items: center;

  label {
    font-size: 12px;
    line-height: 18px;
    display: flex;
    gap: 4px;
    display: flex;
    gap: 12px;
    align-items: center;

    @media (${device.mobile}) {
      line-height: 18px;
    }

    a {
      color: ${({ theme }) => theme.secondaryColor};
      text-decoration: none;
    }
  }

  .tooltip {
    width: 20px;
    height: 20px;
    position: relative;
    margin-left: 4px;
    z-index: 1;

    text-align: center;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;

    & > .tooltip-content {
      position: absolute;
      border-radius: 12px;
      left: 50%;
      transform: translateX(-50%);
      bottom: 24px;
      text-align: center;
      background: ${({ theme }) => theme.whiteColor};
      box-shadow:
        0px 0px 10px 0px rgba(0, 0, 0, 0.4),
        0px 0px 20px 0px rgba(0, 0, 0, 0.4);
      display: none;
      width: 240px;
      padding: 12px;
      justify-content: center;
      align-items: center;

      @media (${device.mobileS}) {
        width: 200px;
        left: auto;
        right: 0;
        transform: translateX(0);
      }
    }

    &:hover {
      & > .tooltip-content {
        display: flex;
      }
    }
  }
`;

export const ScCheckbox = styled.div`
  width: 20px;
  height: 20px;
  min-width: 20px;
  display: block;

  .checkbox {
    display: block;
    border-radius: 4px;
    background: #fff;
    width: 100%;
    height: 100%;
    cursor: pointer;
  }

  input {
    display: none !important;
  }

  span {
    border-width: 1px;
    border-style: solid;
    border-color: ${({ theme }) => theme.blackLightColor};
    box-sizing: border-box;
    transition:
      background-color 0.3s,
      border-color 0.3s;
  }

  input:checked ~ span {
    background: ${({ theme }) =>
      `url(${tickIconUrl}) center no-repeat, ${theme.mainColor}`};
    border-color: ${({ theme }) => theme.mainColor};

    @media (${device.mobile}) {
      background-size: 16px;
    }
  }

  @media (hover: hover) {
    &:hover {
      span {
        border-color: ${({ theme }) => theme.mainColor};
      }

      input:checked ~ span {
        background-color: ${({ theme }) => theme.mainColorHover};
        border-color: ${({ theme }) => theme.mainColorHover};
      }
    }
  }
`;

export const ScTextArea = styled.textarea<ScInputProps & { height?: string }>`
  resize: none;
  height: ${({ height }) => height || '140px'};
  border-radius: 24px;
  width: 100%;
  border-width: 2px;
  border-style: solid;
  border-color: ${({ error }) => (error ? 'red' : 'transparent')};
  padding: ${({ icon }) => (icon ? '12px 16px 12px 36px' : '6px 16px')};
  font-size: 16px;
  line-height: 24px;
  transition:
    padding 0.2s,
    border-color 0.3s;
  appearance: none;
  color: ${({ theme, mode }) =>
    mode === ThemeMode.Dark ? theme.whiteColor : theme.blackColor};
  background-color: ${({ theme, mode }) =>
    mode === ThemeMode.Dark ? theme.grayDarkColor : theme.gray1Color};
  display: block;

  &:disabled {
    color: ${({ theme, mode }) =>
      mode === ThemeMode.Dark
        ? hexToRGB(theme.whiteColor, 0.2)
        : hexToRGB(theme.blackColor, 0.2)};

    &::placeholder {
      color: ${({ theme, mode }) =>
        mode === ThemeMode.Dark
          ? hexToRGB(theme.whiteColor, 0.2)
          : theme.blackLightColor};
    }
  }

  &::placeholder {
    color: ${({ theme, mode }) =>
      mode === ThemeMode.Dark
        ? hexToRGB(theme.whiteColor, 0.2)
        : theme.blackLightColor};
    transition: color 0.3s;
  }

  &:focus {
    border-color: ${({ theme, mode }) =>
      mode === ThemeMode.Dark ? theme.grayColor : theme.grayMegaLightColor};
    color: ${({ theme, mode }) =>
      mode === ThemeMode.Dark ? theme.whiteColor : theme.blackColor};

    &::placeholder {
      color: ${({ theme, mode }) =>
        mode === ThemeMode.Dark ? theme.whiteColor : theme.blackColor};
    }
  }

  @media (hover: hover) {
    &:hover {
      border-color: ${({ theme, value, error, mode }) =>
        value &&
        !error &&
        (mode === ThemeMode.Dark ? theme.grayColor : theme.grayMegaLightColor)};
      &::placeholder {
        color: ${({ theme, mode }) =>
          mode === ThemeMode.Dark ? theme.whiteColor : theme.blackColor};
      }
    }
  }
`;

export const ScSelectSearch = styled.div`
  background: #fafafa;
  padding: 4px 16px;
  margin: 0px -16px 16px;

  border-width: 1px;
  border-style: solid;
  border-color: ${({ theme }) => theme.grayMegaLightColor};
  border-top: none;
  border-left: none;
  border-right: none;

  input {
    width: 100%;
    height: 30px;
    background: transparent;
    border: transparent;

    font-size: 14px;
    line-height: 1.5;
    color: ${({ theme }) => theme.blackColor};
    appearance: none;
  }
`;

export const ScProfileSelectBlock = styled(ScSelectBlock)`
  & > .select-button {
    background-color: ${({ theme, selected, secondary }) =>
      selected
        ? theme.whiteColor
        : secondary
          ? theme.color3
          : theme.grayLightColor};
    color: ${({ theme, selected }) =>
      selected ? theme.blackColor : theme.whiteColor};
    border: ${({ theme, active, selected }) =>
      `2px solid ${
        active
          ? selected
            ? theme.grayMegaLightColor
            : theme.grayColor
          : 'transparent'
      }`};

    & > .value {
      .placeholder {
        color: ${({ theme, active }) =>
          active ? theme.whiteColor : theme.blackLightColor};
      }
    }

    & > .cross {
      position: absolute;
      right: 4px;
      top: 50%;
      transform: translateY(-50%);
    }

    @media (hover: hover) {
      &:hover {
        border-color: ${({ theme, active }) =>
          active ? theme.grayColor : 'transparent'};

        & > .value {
          color: ${({ theme, selected }) =>
            !selected ? theme.whiteColor : theme.blackColor};

          .placeholder {
            color: ${({ theme }) => theme.whiteColor};
          }
        }

        & > .arrow {
          svg {
            path {
              stroke: ${({ theme }) => theme.whiteColor};
            }
          }
        }

        & > .cross {
          svg path {
            stroke: ${({ theme }) => theme.blackColor};
          }
        }
      }
    }
  }
`;

export const ScProfileOptions = styled(ScOptions)`
  background: ${({ theme }) => theme.grayLightColor};
  border: 2px solid ${({ theme }) => theme.grayColor};
  padding: 0 6px;

  ul {
    li {
      &:first-child {
        margin-top: 8px;
      }

      &:last-child {
        margin-bottom: 8px;
      }

      padding: 8px !important;

      &.active,
      &:hover {
        color: ${({ theme }) => theme.whiteColor};
      }

      &.active {
        background-color: ${({ theme }) => theme.blackColor};
        border-radius: 360px;
        width: calc(100% - 8px);
      }
    }
  }
`;
